import { useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { MapPin, Mail, Phone, Code2, Layers, Zap } from 'lucide-react';
import { readData } from '@/firebase/database';

const About = () => {
  const [profile, setProfile] = useState<Record<string, string> | null>(null);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    readData('profile').then(setProfile);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const highlights = [
    { icon: Code2, label: 'Clean Code', value: 'Always' },
    { icon: Layers, label: 'Architecture', value: 'Scalable' },
    { icon: Zap, label: 'Performance', value: 'Optimized' },
  ];

  return (
    <section id="about" className="py-16 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-16"
        >
          <p className="section-tag text-xs">Get to know me</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
            About <span className="gradient-text">Me</span>
          </h2>
          <div className="w-20 h-0.5 mx-auto mt-4 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="glass neon-border rounded-2xl p-4 sm:p-6 font-mono text-xs sm:text-sm relative overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-foreground/20" />
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-foreground/15" />
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-foreground/10" />
                <span className="ml-2 text-muted-foreground text-[10px] sm:text-xs">about.js</span>
              </div>
              <div className="space-y-0.5 sm:space-y-1 text-[11px] sm:text-sm">
                <p><span className="text-primary">const</span> <span className="text-foreground/70">developer</span> = {'{'}</p>
                <p className="pl-4 sm:pl-6"><span className="text-muted-foreground">name</span>: <span className="text-foreground/60">"{profile?.name || 'Mustakim Nur'}"</span>,</p>
                <p className="pl-4 sm:pl-6"><span className="text-muted-foreground">role</span>: <span className="text-foreground/60">"{profile?.role || 'Full-Stack Developer'}"</span>,</p>
                <p className="pl-4 sm:pl-6"><span className="text-muted-foreground">location</span>: <span className="text-foreground/60">"{profile?.location || 'Dhaka, Bangladesh'}"</span>,</p>
                <p className="pl-4 sm:pl-6"><span className="text-muted-foreground">experience</span>: <span className="text-foreground/80">{profile?.yearsExperience || '3'}+</span>,</p>
                <p className="pl-4 sm:pl-6"><span className="text-muted-foreground">available</span>: <span className="text-primary">true</span></p>
                <p>{'}'};</p>
              </div>
              <div className="absolute inset-0 metallic-shimmer pointer-events-none" />
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-3 sm:mt-4">
              {highlights.map(({ icon: Icon, label, value }) => (
                <div key={label} className="glass neon-border rounded-xl p-3 sm:p-4 text-center card-hover">
                  <Icon size={16} className="text-primary mx-auto mb-1.5 sm:mb-2" />
                  <p className="text-[10px] sm:text-xs text-muted-foreground font-mono">{label}</p>
                  <p className="text-xs sm:text-sm font-bold gradient-text">{value}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
            <motion.h3 variants={itemVariants} className="font-display text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
              Building the <span className="gradient-text">digital future</span>, one line at a time.
            </motion.h3>

            <motion.p variants={itemVariants} className="text-muted-foreground text-sm leading-relaxed mb-3 sm:mb-4">
              {profile?.bio || "I'm a passionate Full-Stack Developer with 3+ years of experience crafting exceptional digital experiences."}
            </motion.p>

            <motion.p variants={itemVariants} className="text-muted-foreground text-sm leading-relaxed mb-6 sm:mb-8">
              When I'm not coding, you'll find me contributing to open-source, writing technical articles, or exploring the latest in web technologies.
            </motion.p>

            <motion.div variants={itemVariants} className="space-y-3 mb-6 sm:mb-8">
              {[
                { icon: Mail, label: 'Email', value: profile?.email || 'mustakim@example.com', href: `mailto:${profile?.email}` },
                { icon: Phone, label: 'Phone', value: profile?.phone || '+880 1234-567890', href: `tel:${profile?.phone}` },
                { icon: MapPin, label: 'Location', value: profile?.location || 'Dhaka, Bangladesh', href: '#' },
              ].map(({ icon: Icon, label, value, href }) => (
                <a key={label} href={href} className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors duration-300 group">
                  <div className="p-2 rounded-lg border border-border group-hover:border-primary/30 transition-colors duration-300">
                    <Icon size={14} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs font-mono text-muted-foreground">{label}</p>
                    <p className="text-xs sm:text-sm font-medium text-foreground">{value}</p>
                  </div>
                </a>
              ))}
            </motion.div>

            <motion.button
              variants={itemVariants}
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-neon-solid text-xs sm:text-sm"
            >
              Let's Work Together
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
